import { useGetAffectationsQuery } from '@/features/api/endpoints/affectationEndpoints';
import type { Permission } from '@/models/types';
import type { PermissionId } from '@/shared/auth/permissions';
import { useGetCurrentUser } from '@/shared/auth/useDirectusUser';
import isObject from 'lodash/isObject';
import { useMemo } from 'react';

export const useUserAffectations = () => {
  const { user } = useGetCurrentUser();

  return useGetAffectationsQuery(
    {
      fields: [
        '*',
        {
          role: [
            '*',
            {
              permissions: [
                {
                  Permission_id: ['*'],
                },
              ],
            },
          ],
          etablissement: ['finess', 'name'],
        },
      ],
      filter: {
        user: {
          _eq: user?.id,
        },
      },
      sort: ['-date_created'],
    },
    {
      skip: !user?.id,
    },
  );
};

export const useUserPermissions = () => {
  const { data: affectations = [], isLoading } = useUserAffectations();

  return useMemo(() => {
    const permissions: Permission[] = [];

    affectations.forEach(affectation => {
      if (affectation.status === 'published' && isObject(affectation.role)) {
        if (Array.isArray(affectation.role.permissions)) {
          affectation.role.permissions.forEach(permission => {
            if (isObject(permission.Permission_id)) {
              if (!permissions.some(p => p.id === permission.Permission_id.id)) {
                permissions.push(permission.Permission_id);
              }
            }
          });
        }
      }
    });
    permissions.sort((a, b) => a.id.localeCompare(b.id));
    return {
      permissions,
      isLoading,
    };
  }, [affectations, isLoading]);
};

export const useUserHasEveryPermissions = (permissionsToCheck: PermissionId[]) => {
  const { permissions = [], isLoading } = useUserPermissions();

  const result = permissionsToCheck.every(permissionToCheck =>
    permissions.some(permission => permission.id === permissionToCheck),
  );
  return {
    result,
    isLoading,
  };
};

export const userHasSomePermissions = (
  userPermissions: Permission[],
  permissionsToCheck: PermissionId[],
) => {
  return permissionsToCheck.some(permissionToCheck =>
    userPermissions.some(permission => permission.id === permissionToCheck),
  );
};

export const useUserHasSomePermissions = (permissionsToCheck: PermissionId[]) => {
  const { permissions = [], isLoading } = useUserPermissions();
  const result = userHasSomePermissions(permissions, permissionsToCheck);
  return {
    result,
    isLoading,
  };
};

export const useUserHasPermission = (permissionToCheck: PermissionId) => {
  return useUserHasEveryPermissions([permissionToCheck]);
};

export const userHasRoles = (userRoles: number[], rolesToCheck: number[]) => {
  return rolesToCheck.some(roleToCheck => userRoles.includes(roleToCheck));
};

export const userHasRole = (userRoles: number[], roleToCheck: number) => {
  return userHasRoles(userRoles, [roleToCheck]);
};

export const useUserHasRoles = (rolesToCheck: number[]) => {
  const { data: affectations = [], isLoading } = useUserAffectations();
  const userRoles = useMemo(() => {
    const roles: number[] = [];
    affectations.forEach(affectation => {
      if (affectation.status === 'published' && isObject(affectation.role)) {
        const role = affectation.role;
        if (!roles.some(r => r === role.id)) {
          roles.push(role.id);
        }
      }
    });
    return roles;
  }, [affectations]);

  if (rolesToCheck.length === 0) {
    return {
      data: true,
      isLoading: false,
    };
  }

  const data = userHasRoles(userRoles, rolesToCheck);
  return {
    data,
    isLoading,
  };
};
